import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Languages, Plus, Trash2, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Translations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ english: "", mr: "", hi: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/translations");
      setRows(r.data.translations || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.english.trim()) { toast.error("English phrase required"); return; }
    try {
      await api.post("/translations", form);
      toast.success("Saved");
      setForm({ english: "", mr: "", hi: "" });
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (row) => {
    if (!confirm(`Delete translation for "${row.english}"?`)) return;
    try {
      await api.delete(`/translations/${row.id}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const edit = (row) => {
    setForm({ id: row.id, english: row.english, mr: row.mr || "", hi: row.hi || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="overline">DICTIONARY</div>
      <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900 flex items-center gap-3">
        <Languages size={26} className="text-pink-600" /> Custom translations
      </h1>
      <p className="mt-2 text-sm text-slate-600 max-w-xl">
        Add your own frequently-used phrases in Marathi and Hindi. They automatically appear on the
        bilingual prescription printout alongside the built-in clinical dictionary. Your custom entries
        take precedence over the defaults so you can override or refine them.
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="translation-form">
        <div className="section-title">{form.id ? "Edit phrase" : "Add a new phrase"}</div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <Label className="overline">ENGLISH</Label>
            <Input value={form.english} onChange={(e) => setForm({ ...form, english: e.target.value })} placeholder="e.g. Check sugar before insulin" data-testid="tr-english" />
          </div>
          <div>
            <Label className="overline">MARATHI</Label>
            <Input value={form.mr} onChange={(e) => setForm({ ...form, mr: e.target.value })} placeholder="मराठी अनुवाद" data-testid="tr-mr" />
          </div>
          <div>
            <Label className="overline">HINDI</Label>
            <Input value={form.hi} onChange={(e) => setForm({ ...form, hi: e.target.value })} placeholder="हिंदी अनुवाद" data-testid="tr-hi" />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={save} className="bg-pink-600 hover:bg-pink-700 text-white gap-2" data-testid="tr-save-btn">
            {form.id ? <><Save size={14} /> Save changes</> : <><Plus size={14} /> Add phrase</>}
          </Button>
          {form.id && <Button variant="outline" onClick={() => setForm({ english: "", mr: "", hi: "" })}>Cancel</Button>}
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="translations-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2 w-1/3">English</th>
              <th className="px-4 py-2 w-1/3">मराठी</th>
              <th className="px-4 py-2 w-1/3">हिंदी</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-6 text-center text-slate-500 text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-sm text-slate-500">
                No custom translations yet. Add your first phrase above.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer" onClick={() => edit(r)} data-testid={`tr-row-${r.id}`}>
                <td className="px-4 py-2 font-medium">{r.english}</td>
                <td className="px-4 py-2 text-slate-700">{r.mr || <span className="text-slate-300 italic">—</span>}</td>
                <td className="px-4 py-2 text-slate-700">{r.hi || <span className="text-slate-300 italic">—</span>}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 rounded text-red-600 hover:bg-red-50" data-testid={`tr-del-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
